import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Search, X, Clock, Star, Zap } from 'lucide-react';
import { useClickOutside } from '../../hooks/useClickOutside';
import { Prompt } from '../../types';

interface SmartSearchBarProps {
  searchQuery: string;
  onSearchChange: (query: string) => void;
  prompts?: Prompt[];
  recentlyExecuted?: Array<{ promptId: string; executedAt: Date; promptSnapshot: Prompt }>;
}

interface SearchSuggestion {
  text: string;
  type: 'recent_search' | 'prompt_title' | 'prompt_content' | 'keyword' | 'platform';
  score: number;
  prompt?: Prompt;
  icon?: React.ReactNode;
}

interface SearchHistory {
  query: string;
  timestamp: number;
  count: number;
}

export const SmartSearchBar: React.FC<SmartSearchBarProps> = ({
  searchQuery,
  onSearchChange,
  prompts = [],
  recentlyExecuted = [],
}) => {
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [searchHistory, setSearchHistory] = useState<SearchHistory[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);

  const suggestionsRef = useClickOutside(() => {
    setShowSuggestions(false);
  });

  // Load search history from localStorage
  useEffect(() => {
    try {
      const saved = localStorage.getItem('promptos_search_history');
      if (saved) {
        const history = JSON.parse(saved);
        setSearchHistory(history || []);
      }
    } catch (error) {
      console.warn('Error loading search history:', error);
    }
  }, []);

  // Save search to history
  const saveToSearchHistory = (query: string) => {
    if (!query.trim() || query.length < 2) return;

    const trimmedQuery = query.trim().toLowerCase();
    
    setSearchHistory(prev => {
      const existing = prev.find(h => h.query === trimmedQuery);
      let updated;
      
      if (existing) {
        // Update existing search
        updated = prev.map(h => 
          h.query === trimmedQuery 
            ? { ...h, timestamp: Date.now(), count: h.count + 1 }
            : h
        );
      } else {
        // Add new search
        updated = [...prev, { query: trimmedQuery, timestamp: Date.now(), count: 1 }];
      }
      
      // Keep only last 50 searches
      const sorted = updated
        .sort((a, b) => b.timestamp - a.timestamp)
        .slice(0, 50);
      
      try {
        localStorage.setItem('promptos_search_history', JSON.stringify(sorted));
      } catch (error) {
        console.warn('Error saving search history:', error);
      }
      
      return sorted;
    });
  };

  // Extract keywords from prompts for content-based suggestions
  const extractKeywords = (text: string): string[] => {
    // Remove common stop words and extract meaningful terms
    const stopWords = new Set(['the', 'a', 'an', 'and', 'or', 'but', 'in', 'on', 'at', 'to', 'for', 'of', 'with', 'by', 'is', 'are', 'was', 'were', 'be', 'been', 'have', 'has', 'had', 'do', 'does', 'did', 'will', 'would', 'could', 'should', 'may', 'might', 'can', 'this', 'that', 'these', 'those']);
    
    return text
      .toLowerCase()
      .replace(/[^\w\s]/g, ' ')
      .split(/\s+/)
      .filter(word => word.length > 2 && !stopWords.has(word))
      .slice(0, 10); // Limit to prevent too many keywords
  };

  // Generate intelligent suggestions based on current input and user patterns
  const generateSuggestions = useMemo(() => {
    if (!searchQuery.trim()) {
      // When no search query, show recent searches and popular content
      const suggestions: SearchSuggestion[] = [];
      
      // Recent searches (top 5)
      const recentSearches = searchHistory
        .sort((a, b) => b.timestamp - a.timestamp)
        .slice(0, 5)
        .map(h => ({
          text: h.query,
          type: 'recent_search' as const,
          score: 100 + h.count, // High score for recent
          icon: <Clock className="h-4 w-4 text-gray-400" />
        }));
      
      suggestions.push(...recentSearches);
      
      // Recently executed prompts
      const recentPrompts = recentlyExecuted
        .slice(0, 3)
        .map(re => ({
          text: re.promptSnapshot.title,
          type: 'prompt_title' as const,
          score: 90,
          prompt: re.promptSnapshot,
          icon: <Zap className="h-4 w-4 text-blue-500" />
        }));
      
      suggestions.push(...recentPrompts);
      
      // Favorite prompts
      const favoritePrompts = prompts
        .filter(p => p.isFavorite)
        .slice(0, 3)
        .map(p => ({
          text: p.title,
          type: 'prompt_title' as const,
          score: 85,
          prompt: p,
          icon: <Star className="h-4 w-4 text-yellow-500" />
        }));
      
      suggestions.push(...favoritePrompts);
      
      return suggestions
        .sort((a, b) => b.score - a.score)
        .slice(0, 8);
    }

    // When user is typing, generate contextual suggestions
    const query = searchQuery.toLowerCase().trim();
    const suggestions: SearchSuggestion[] = [];
    
    // Score prompts based on multiple factors
    prompts.forEach(prompt => {
      let score = 0;
      
      // Title match (highest priority)
      if (prompt.title.toLowerCase().includes(query)) {
        score += 100;
        if (prompt.title.toLowerCase().startsWith(query)) {
          score += 50; // Bonus for prefix match
        }
      }
      
      // Content match
      if (prompt.body.toLowerCase().includes(query)) {
        score += 50;
      }
      
      // Platform match
      if (prompt.aiPlatform?.toLowerCase().includes(query)) {
        score += 30;
      }
      
      // Boost score based on user behavior
      if (prompt.isFavorite) score += 25;
      if (prompt.lastExecutedAt) {
        const daysSinceExecution = (Date.now() - prompt.lastExecutedAt.getTime()) / (1000 * 60 * 60 * 24);
        if (daysSinceExecution < 7) score += 20;
        if (daysSinceExecution < 1) score += 10;
      }
      
      // Boost for recent prompts
      const daysSinceCreated = (Date.now() - prompt.createdAt.getTime()) / (1000 * 60 * 60 * 24);
      if (daysSinceCreated < 7) score += 15;
      
      // Boost for templates
      if (prompt.isTemplate) score += 10;
      
      // Check search history patterns
      const relatedSearches = searchHistory.filter(h => 
        h.query.includes(query) || query.includes(h.query)
      );
      if (relatedSearches.length > 0) {
        score += relatedSearches.reduce((sum, h) => sum + h.count, 0) * 5;
      }
      
      if (score > 0) {
        suggestions.push({
          text: prompt.title,
          type: 'prompt_title',
          score,
          prompt,
          icon: prompt.isFavorite ? 
            <Star className="h-4 w-4 text-yellow-500 fill-current" /> :
            prompt.lastExecutedAt ? 
            <Zap className="h-4 w-4 text-blue-500" /> :
            undefined
        });
      }
    });
    
    // Extract and score keywords from user's prompt library
    const keywordCounts: Record<string, { count: number; prompts: Prompt[] }> = {};
    
    prompts.forEach(prompt => {
      const keywords = extractKeywords(`${prompt.title} ${prompt.body}`);
      keywords.forEach(keyword => {
        if (keyword.includes(query)) {
          if (!keywordCounts[keyword]) {
            keywordCounts[keyword] = { count: 0, prompts: [] };
          }
          keywordCounts[keyword].count++;
          keywordCounts[keyword].prompts.push(prompt);
        }
      });
    });
    
    // Add keyword suggestions
    Object.entries(keywordCounts)
      .filter(([keyword, data]) => data.count >= 2) // Only suggest if appears in multiple prompts
      .forEach(([keyword, data]) => {
        let score = data.count * 10;
        
        // Boost based on recent usage of related prompts
        const hasRecentUsage = data.prompts.some(p => 
          p.lastExecutedAt && (Date.now() - p.lastExecutedAt.getTime()) < 7 * 24 * 60 * 60 * 1000
        );
        if (hasRecentUsage) score += 20;
        
        suggestions.push({
          text: keyword,
          type: 'keyword',
          score,
          icon: <Search className="h-4 w-4 text-gray-400" />
        });
      });
    
    // Add recent search matches
    searchHistory
      .filter(h => h.query.includes(query) || query.includes(h.query))
      .forEach(h => {
        suggestions.push({
          text: h.query,
          type: 'recent_search',
          score: 60 + h.count * 5,
          icon: <Clock className="h-4 w-4 text-gray-400" />
        });
      });
    
    return suggestions
      .sort((a, b) => b.score - a.score)
      .slice(0, 8);
  }, [searchQuery, prompts, recentlyExecuted, searchHistory]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    onSearchChange(value);
    setShowSuggestions(value.length >= 0); // Show suggestions as soon as user starts typing
  };

  const handleSuggestionClick = (suggestion: SearchSuggestion) => {
    onSearchChange(suggestion.text);
    saveToSearchHistory(suggestion.text);
    setShowSuggestions(false);
    inputRef.current?.focus();
  };

  const clearSearch = () => {
    onSearchChange('');
    setShowSuggestions(false);
    inputRef.current?.focus();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setShowSuggestions(false);
      inputRef.current?.blur();
    } else if (e.key === 'Enter') {
      if (searchQuery.trim()) {
        saveToSearchHistory(searchQuery);
      }
      setShowSuggestions(false);
    }
  };

  const handleFocus = () => {
    setShowSuggestions(true);
  };

  const handleBlur = () => {
    // Save search to history when user finishes typing
    if (searchQuery.trim()) {
      saveToSearchHistory(searchQuery);
    }
  };

  const getSuggestionTypeLabel = (type: string) => {
    switch (type) {
      case 'recent_search':
        return 'Recent search';
      case 'prompt_title':
        return 'Your prompt';
      case 'prompt_content':
        return 'Content match';
      case 'keyword':
        return 'From your library';
      case 'platform':
        return 'Platform';
      default:
        return '';
    }
  };

  return (
    <div className="relative flex-1 max-w-md lg:max-w-2xl">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 
          text-gray-400 dark:text-gray-500 transition-colors duration-300" />
        <input
          ref={inputRef}
          type="text"
          placeholder="Search..."
          value={searchQuery}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={handleFocus}
          onBlur={handleBlur}
          className="w-full pl-8 lg:pl-10 pr-8 lg:pr-10 py-2 lg:py-2.5 bg-gray-50 dark:bg-dark-surface border 
            border-gray-200 dark:border-dark-border rounded-lg focus:outline-none 
            focus:ring-2 focus:ring-fire-500 dark:focus:ring-fire-400 
            focus:border-transparent text-gray-900 dark:text-dark-text 
            placeholder-gray-500 dark:placeholder-gray-400 transition-all duration-300 text-sm lg:text-base"
        />
        {searchQuery && (
          <button
            onClick={clearSearch}
            className="absolute right-2 lg:right-3 top-1/2 transform -translate-y-1/2 
              text-gray-400 dark:text-gray-500 hover:text-gray-600 
              dark:hover:text-gray-300 transition-colors duration-300"
          >
            <X className="h-3 w-3 lg:h-4 lg:w-4" />
          </button>
        )}
      </div>

      {/* Intelligent Suggestions */}
      {showSuggestions && generateSuggestions.length > 0 && (
        <div
          ref={suggestionsRef}
          className="absolute top-full left-0 right-0 mt-1 bg-white dark:bg-dark-surface 
            rounded-lg shadow-lg dark:shadow-dark-card border border-gray-200 
            dark:border-dark-border py-2 z-50 transition-colors duration-300"
        >
          <div className="px-3 py-1 text-xs font-medium text-gray-500 dark:text-gray-400 
            uppercase tracking-wide transition-colors duration-300">
            {searchQuery.trim() ? 'Smart Suggestions' : 'Quick Access'}
          </div>
          
          {generateSuggestions.map((suggestion, index) => (
            <button
              key={`${suggestion.type}-${suggestion.text}-${index}`}
              onClick={() => handleSuggestionClick(suggestion)}
              className="w-full text-left px-3 py-2 hover:bg-gray-50 dark:hover:bg-gray-700 
                text-sm transition-colors duration-200 flex items-center justify-between group"
            >
              <div className="flex items-center flex-1 min-w-0">
                {suggestion.icon && (
                  <div className="mr-3 flex-shrink-0">
                    {suggestion.icon}
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="text-gray-800 dark:text-gray-200 font-medium truncate">
                    {suggestion.text}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    {getSuggestionTypeLabel(suggestion.type)}
                  </div>
                </div>
              </div>
              
              {/* Relevance indicator */}
              {suggestion.score > 80 && (
                <div className="w-2 h-2 bg-fire-500 dark:bg-fire-400 rounded-full opacity-60" />
              )}
            </button>
          ))}
          
          <div className="px-3 py-2 border-t border-gray-100 dark:border-dark-border mt-1">
            <p className="text-xs text-gray-400 dark:text-gray-500 text-center">
              {searchQuery.trim() 
                ? 'Suggestions based on your prompt library and usage patterns'
                : 'Showing your most relevant and recent content'
              }
            </p>
          </div>
        </div>
      )}
    </div>
  );
};